import type { PaymentSortField } from '@/gql/graphql';
import { decimal, int, isoDate, oneOf, optionsOf, text, uuidList } from '@/hooks/useUrlState';

/** Keep the keys in sync with Go's payment statuses (the `status` filter takes these workflow strings). */
export type PaymentStatusValue =
  | 'new'
  | 'waiting'
  | 'pending'
  | 'canceling'
  | 'canceled'
  | 'failure'
  | 'success'
  | 'refunding'
  | 'refunded';
export const PAYMENT_STATUS_LABELS: Record<PaymentStatusValue, string> = {
  new: 'New',
  waiting: 'Waiting',
  pending: 'Pending',
  canceling: 'Canceling',
  canceled: 'Canceled',
  failure: 'Failure',
  success: 'Success',
  refunding: 'Refunding',
  refunded: 'Refunded',
};

export type PaymentMethodValue = 'pin' | 'cash' | 'tab' | 'online' | 'paypal';
export const PAYMENT_METHOD_LABELS: Record<PaymentMethodValue, string> = {
  pin: 'PIN',
  cash: 'Cash',
  tab: 'Tab',
  online: 'Online',
  paypal: 'PayPal',
};

export const PAYMENT_STATUS_OPTIONS = optionsOf(PAYMENT_STATUS_LABELS, 'Any status');
export const PAYMENT_METHOD_OPTIONS = optionsOf(PAYMENT_METHOD_LABELS, 'Any method');

export type PaymentsSortColumn =
  | 'id'
  | 'status'
  | 'method'
  | 'amount'
  | 'tip'
  | 'order'
  | 'tab'
  | 'terminal'
  | 'recognitionDate'
  | 'createdAt';

/** DataGrid column `id` → GraphQL `PaymentSortField` for server-side sort; its keys are the values `sort` accepts. */
export const PAYMENT_COLUMN_SORT: Record<PaymentsSortColumn, PaymentSortField> = {
  id: 'ID',
  status: 'STATUS',
  method: 'PAYMENT_METHOD',
  amount: 'AMOUNT',
  tip: 'AMOUNT_TIP',
  order: 'ORDER_ID',
  tab: 'TAB_NAME',
  terminal: 'TERMINAL_NAME',
  recognitionDate: 'RECOGNITION_DATE',
  createdAt: 'CREATED_AT',
};

export const PAYMENTS_DEFAULT_SORT: { sort: PaymentsSortColumn; dir: 'asc' | 'desc' } = { sort: 'createdAt', dir: 'desc' };

/** The /payments query string, in this order (see README "List pages, URL state"). */
export const paymentsUrlSchema = {
  search: text(),
  status: oneOf(PAYMENT_STATUS_LABELS, ''),
  method: oneOf(PAYMENT_METHOD_LABELS, ''),
  amountMin: decimal(),
  amountMax: decimal(),
  tipMin: decimal(),
  tipMax: decimal(),
  from: isoDate(),
  to: isoDate(),
  terminals: uuidList(),
  sort: oneOf(PAYMENT_COLUMN_SORT, PAYMENTS_DEFAULT_SORT.sort),
  dir: oneOf<'asc' | 'desc', 'asc' | 'desc'>({ asc: true, desc: true }, PAYMENTS_DEFAULT_SORT.dir),
  page: int({ min: 1 }, 1),
  size: int({ min: 1, max: 500 }, 50),
};

/** The keys that live in the collapsible Filters panel: the panel opens on mount when any is set. */
export const PAYMENTS_ADVANCED_KEYS = [
  'status',
  'method',
  'amountMin',
  'amountMax',
  'tipMin',
  'tipMax',
  'from',
  'to',
  'terminals',
] as const satisfies readonly (keyof typeof paymentsUrlSchema)[];
